import { Slide, toast } from "react-toastify";
import type { ToastOptions } from "react-toastify";

/**
 * `overrides` is merged over the defaults below - e.g. { autoClose: false,
 * toastId: "..." } for a persistent, de-duplicated notice
 * (PageTypesUpdateContainer.jsx, SCRUM-855).
 */
const NotificationAlertOptions = (
  type: string,
  message: string,
  overrides: ToastOptions = {}
) => {
  const toastOptions: ToastOptions = {
    position: "top-center",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    theme: "colored",
    transition: Slide,
    ...overrides,
  };

  if (type === "success") {
    toast.success( message, toastOptions );
    }
    else if (type === "error") {
    toast.error(message , toastOptions);
    }
    else if (type === "info") {
    toast.info(message, toastOptions);
    }
    else if (type === "warning") {
    toast.warning(message, toastOptions);
    }
    else {
    toast(message, toastOptions);
    }
};

/**
 * Report a "soft failure": something degraded to a safe fallback instead of
 * crashing (a filter got disabled, a default got used), but the result isn't
 * fully correct and the user should know rather than be quietly handed
 * wrong-looking-but-valid data. Shows a persistent, dismissable toast with an
 * error code the user can report to IT - there's no error-monitoring tool
 * (Sentry or similar) wired into this app to capture these automatically
 * (SCRUM-855), so this is the pragmatic middle ground: name the problem
 * visibly now via a shared, reusable helper, without requiring a full fix on
 * the spot - the fix can come later once the failure is at least on record.
 *
 * `toastId` is derived from `code`, so repeated calls for the same code
 * update the existing toast instead of stacking a new one on top.
 *
 * `transient: true` appends "this usually resolves itself" instead of
 * treating it as a hard error - use it when the fallback is caused by data
 * that probably hasn't finished loading yet, not by something permanently
 * wrong (a "reload" suggestion is actively misleading in the permanent case).
 */
export const reportSoftFailure = ({
  code,
  message,
  transient = false,
}: {
  code: string;
  message: string;
  transient?: boolean;
}) => {
  const suffix = transient
    ? " This usually resolves itself — reload the page if it's still happening after a bit."
    : "";
  NotificationAlertOptions("warning", `${message}${suffix} (Code: ${code})`, {
    autoClose: false,
    toastId: `soft-failure-${code}`,
  });
};

export default NotificationAlertOptions;
